import React, { useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
  Chip,
  TextField,
  InputAdornment,
  Typography,
  Divider,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { Listing } from '@/types';

interface ListingMultiSelectProps {
  listings: Listing[];
  selectedListings: string[];
  onChange: (selectedIds: string[]) => void;
  loading?: boolean;
  error?: string;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 350,
    },
  },
};

export const ListingMultiSelect: React.FC<ListingMultiSelectProps> = ({
  listings,
  selectedListings,
  onChange,
  loading = false,
  error,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter listings based on search term
  const filteredListings = listings.filter(listing =>
    listing.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    listing.address.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = () => {
    const allListingIds = filteredListings.map(listing => listing.listingId);
    onChange(allListingIds);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const handleChange = (event: any) => {
    const value = event.target.value;
    onChange(typeof value === 'string' ? value.split(',') : value);
  };

  const getSelectedListingNames = () => {
    return selectedListings
      .map(id => listings.find(listing => listing.listingId === id)?.title)
      .filter(Boolean)
      .join(', ');
  };

  const renderValue = (selected: string[]) => {
    if (selected.length === 0) {
      return <Typography color="text.secondary">Select listings...</Typography>;
    }
    
    if (selected.length === listings.length) {
      return `All listings (${listings.length})`;
    }
    
    if (selected.length <= 2) {
      return getSelectedListingNames();
    }
    
    return `${selected.length} listings selected`;
  };

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error" variant="body2">
          Error loading listings: {error}
        </Typography>
      </Box>
    );
  }

  if (listings.length === 0 && !loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary" variant="body2">
          No listings available
        </Typography>
      </Box>
    );
  }

  return (
    <FormControl sx={{ minWidth: 300, maxWidth: 400 }}>
      <InputLabel id="listing-multi-select-label">Filter by Listings</InputLabel>
      <Select
        labelId="listing-multi-select-label"
        id="listing-multi-select"
        multiple
        value={selectedListings}
        onChange={handleChange}
        input={<OutlinedInput label="Filter by Listings" />}
        renderValue={renderValue}
        MenuProps={MenuProps}
        disabled={loading}
      >
        {/* Search field */}
        <MenuItem disableRipple sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
          <TextField
            size="small"
            placeholder="Search listings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: '100%' }}
          />
        </MenuItem>
        
        <Divider />
        
        {/* Select All / Clear All options */}
        <MenuItem onClick={handleSelectAll} disabled={filteredListings.length === 0}>
          <ListItemText 
            primary="Select All" 
            secondary={`${filteredListings.length} listings`}
          />
        </MenuItem>
        
        <MenuItem onClick={handleClearAll} disabled={selectedListings.length === 0}>
          <ListItemText 
            primary="Clear All" 
            secondary={selectedListings.length > 0 ? `Clear ${selectedListings.length} selected` : undefined}
          />
        </MenuItem>
        
        <Divider />
        
        {/* Listing options */}
        {filteredListings.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary="No listings found" />
          </MenuItem>
        ) : (
          filteredListings.map((listing) => (
            <MenuItem key={listing.listingId} value={listing.listingId}>
              <Checkbox 
                checked={selectedListings.indexOf(listing.listingId) > -1}
                size="small"
              />
              <ListItemText 
                primary={listing.title}
                secondary={`${listing.address.city}, ${listing.address.state}`}
                sx={{ ml: 1 }}
              />
            </MenuItem>
          ))
        )}
        
        {/* Show count at bottom */}
        {filteredListings.length > 0 && (
          <>
            <Divider />
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                {selectedListings.length} of {listings.length} listings selected
              </Typography>
            </MenuItem>
          </>
        )}
      </Select>
    </FormControl>
  );
};